import {Rect} from "../../Utils/ThreeJS/VectorStructs.js";

export const CHUNK_N = 33; // N×N samples per chunk
/** Multiplier over the minimum chunk count needed to cover the viewport.
 *  Extra capacity keeps recently-used off-screen chunks alive for smooth panning. */
const LRU_SAFETY_MULTIPLIER = 4;

/** Set15.5 P3 (§5.2) — hard, byte-derived ceiling on the number of LIVE chunks.  NOT user-facing,
 *  NOT derived from view distance: it is an OOM guard sized from the per-chunk footprint.  Per
 *  chunk ≈ 100 KB (`heights` + `geoHeightGeom` 33²×4 B each + `normals` 33²×3×4 B ≈ 22 KB CPU, plus
 *  six+ per-chunk GPU DataTextures at TerrainRenderer.tsx:658–678 ≈ 30–90 KB).  So the worst-case
 *  resident footprint is `MAX_CHUNKS_HARD_CEILING × ~100 KB`.  The pre-P3 cap (8192, derived from
 *  `maxViewDistance / 16`) was already ~800 MB and was never sized against memory; 16384 is
 *  ~1.6 GB worst case.  Per R1 this must NEVER be a persisted/knob value — a module constant. */
export let MAX_CHUNKS_HARD_CEILING = 16384;
/** Set15.5 P6 — unit-test hook.  Test-only: lowers the ceiling so the halt-on-ceiling path engages
 *  with a small resident footprint instead of a 16k-chunk allocation.  Returns the previous value so
 *  tests can restore it.  Non-test production callers should never use this. */
export function __TestOnly_SetMaxChunksHardCeiling(v: number): number {
	const prev = MAX_CHUNKS_HARD_CEILING;
	MAX_CHUNKS_HARD_CEILING = v;
	return prev;
}
/** Set15.5 P3 (§5.3 / R5) — exponential-smoothing rate for `lodCoarseningFactor` toward its target
 *  each frame.  Kept small so a static viewpoint converges to a fixed point without LOD pumping
 *  (visible frame-to-frame coarsen/refine oscillation). */
const LOD_COARSEN_HYSTERESIS = 0.25;

export class Chunk {
	heights = new Float32Array(CHUNK_N * CHUNK_N);
	normals = new Float32Array(CHUNK_N * CHUNK_N * 3);
	/** Set15 P3 (§3.4) — the D-LESS height lattice (CHUNK_N×CHUNK_N), the field the 3D vertex
	 *  shader displaces geometry by (`u_geoHeightGeom`).  `heights` keeps D (the mesh/overlay +
	 *  the `geoHeight` water primitive see true elevation); this separate lattice excludes D so
	 *  geometry displacement doesn't double-count the fragment-rendered ambient detail. */
	geoHeightGeom = new Float32Array(CHUNK_N * CHUNK_N);
	/** 2026-08-08 (issue 2) — per-sample ambient-weight mask (CHUNK_N×CHUNK_N), from the first
	 *  mod's multiplier closure (innermost closed-ring wins).  Null = no multiplier ring (the
	 *  shader skips the fetch).  The shader scales its ∇D by this — 0-rings silence D. */
	ambientWeightMask: Float32Array | null = null;
	/** Heights + normals need recomputation (noise params or slope_scale changed). */
	dirty_data = true;
	/** Texture needs regeneration (light/color settings changed, or data was just recomputed). */
	dirty_texture = true;
	/** True while a worker is computing heights+normals for this chunk. Prevents duplicate dispatch. */
	inflight = false;
	/** True while a worker is building the texture bitmap for this chunk. */
	texInflight = false;
	/** Incremented each time dirty_texture is set to true. Lets callbacks detect stale results. */
	texVersion = 0;
	/** Called by LRU on eviction so callers (eg. renderer) can release GPU resources. */
	onEvict: (()=>void)|null = null;
}

export class QuadtreeNode {
	constructor(public bounds: Rect, public depth: number) {}
	children: [QuadtreeNode, QuadtreeNode, QuadtreeNode, QuadtreeNode]|null = null;
	/** Only present on leaf nodes. Lazily evaluated via LRU cache. */
	chunk: Chunk|null = null;

	/** Set15.5 P5/Set16 fix (2026-08-19) — the parent's D-LESS height lattice RETAINED across a
	 *  `split()`.  §6.2 requires the elevation-aware metric's height source to read the nearest
	 *  evaluated ANCESTOR so a freshly-split (not-yet-evaluated) child keeps a stable height — but
	 *  `split()` must destroy the parent's GPU-tied `chunk` (release textures via `onEvict`, or
	 *  they leak), which nulled the parent's ONLY height source and collapsed the metric back to
	 *  `dz = eyeZ`.  That made the tree split→eval→(height source gone)→merge→eval→split forever
	 *  (the §6.2 split/merge churn the plan warned about — confirmed live: `internalWithChunk` was
	 *  always 0, so a just-split child never had an evaluated ancestor to read).
	 *
	 *  This field breaks that loop by keeping the parent's `geoHeightGeom` (a 33×33 Float32Array,
	 *  the SAME field the 3D vertex shader displaces geometry by) alive as a NON-GPU height source,
	 *  separate from the `chunk`/LRU/texture lifecycle.  The ancestor sampler
	 *  (`findDeepestEvaluatedNodeForPoint`/`_sampleGeoHeightGeomFrom`) reads it as the parent's
	 *  "evaluated" height, so a freshly-split child sees a STABLE height instead of `null` → the
	 *  metric's refinement pressure stays consistent → the tree reaches a fixed point rather than
	 *  oscillating.  It is cleared on `merge()` (the folded-up node re-evaluates) and GC'd with the
	 *  tree on dispose — it never enters the LRU, so `chunkCount` is unaffected. */
	retainedGeoHeightGeom: Float32Array|null = null;

	/** Set15.5 P3 (§5.4) — LRU linked-list links.  O(1) `touch`/`remove` via a Map-keyed doubly
	 *  linked list instead of the pre-P3 plain-array `indexOf`/`splice` (O(n)).  Only meaningful
	 *  while the node has a live chunk in the LRU. */
	__lruPrev: QuadtreeNode|null = null;
	__lruNext: QuadtreeNode|null = null;

	get isLeaf() { return this.children == null; }
}

// ---------------------------------------------------------------------------
// LRU cache
// ---------------------------------------------------------------------------

class LRUCache {
	/** Insertion/access order, front = oldest (next to evict).  Set15.5 P3 (§5.4) — a `Map`-keyed
	 *  linked list (via `prev`/`next` on the nodes) so `touch`/`remove` are O(1): the pre-P3 plain
	 *  array used `indexOf`/`splice` (O(n) each), and at thousands of chunks with a `touch` on every
	 *  worker result that was ~8k comparisons per chunk landing (contributing to the fill-in stall). */
	private head: QuadtreeNode|null = null;
	private tail: QuadtreeNode|null = null;
	/** Number of nodes currently in the LRU (maintained O(1) alongside the linked list, so
	 *  `evictIfNeeded` doesn't need to walk the list to count — a pre-P3 O(n) trap). */
	private count = 0;
	/** Set15.5 P3 — the live chunk budget.  Computed by `TerrainQuadtree.update()` from the real
	 *  demand formula (no per-frame cap — §5.1 deleted `maxChunks`), clamped to
	 *  `MAX_CHUNKS_HARD_CEILING`, and never exceeded (the LRU evicts rather than grow past it). */
	maxChunks = 512;

	touch(node: QuadtreeNode) {
		this._unlink(node);
		this._pushTail(node);
		this.evictIfNeeded();
	}

	/** Remove `node`'s chunk entirely.  Used by split()/merge()/structural teardown — `onEvict` IS
	 *  fired so the renderer releases the node's GPU textures (correct for memory: the renderer
	 *  keys its texture maps by `QuadtreeNode`, and a split-away / merged node is no longer a leaf
	 *  the render loop touches, so its textures would leak otherwise — §5.4). */
	remove(node: QuadtreeNode) {
		this._unlink(node);
		node.chunk?.onEvict?.();
		node.chunk = null;
	}

	private _unlink(node: QuadtreeNode) {
		const prev = node.__lruPrev, next = node.__lruNext;
		if (prev) prev.__lruNext = next; else if (this.head === node) this.head = next;
		if (next) next.__lruPrev = prev; else if (this.tail === node) this.tail = prev;
		if (node.__lruPrev !== null || node.__lruNext !== null || this.head === node || this.tail === node) {
			this.count--;
		}
		node.__lruPrev = null;
		node.__lruNext = null;
	}

	private _pushTail(node: QuadtreeNode) {
		node.__lruPrev = this.tail;
		node.__lruNext = null;
		if (this.tail) this.tail.__lruNext = node; else this.head = node;
		this.tail = node;
		this.count++;
	}

	private evictIfNeeded() {
		while (this.head && this.count > this.maxChunks) {
			const oldest = this.head;
			this._unlink(oldest);
			oldest.chunk?.onEvict?.();
			oldest.chunk = null;
		}
	}

	size(): number {
		return this.count;
	}

	clear() {
		let cur = this.head;
		while (cur) {
			const next = cur.__lruNext;
			cur.chunk?.onEvict?.();
			cur.chunk = null;
			cur.__lruPrev = null;
			cur.__lruNext = null;
			cur = next;
		}
		this.head = null;
		this.tail = null;
		this.count = 0;
	}
}

// ---------------------------------------------------------------------------
// Quadtree manager
// ---------------------------------------------------------------------------

/** Pixel size of a world-unit at the current zoom level. Provided by the renderer each frame. */
export type ViewportInfo = {
	/** World-to-screen scale: pixels per world unit, evaluated at a world point (x, y).
	 *  Ortho: constant across the ground plane (byte-identical to the old single-number).
	 *  Persp: `H / (2·d·tan(θ/2))` per node (§3.5) — larger far away. */
	worldToScreenAt(x: number, y: number): number;
	/** Set15.5 P6 (2026-08-19) — under PERSPECTIVE (3D), the LOD budget must NOT be derived from a
	 *  single viewport-centre estimate, because P4's elevation-aware `worldToScreenAt` makes that
	 *  one value pathologically large wherever terrain sits near the eye's height (a peak reaching
	 *  the eye, or — in inverted relief — a valley floor under a low eye).  The area-based demand
	 *  formula (`viewportWidth·wts × viewportHeight·wts`) multiplies that single inflated sample
	 *  across the whole footprint, blowing the budget past `MAX_CHUNKS_HARD_CEILING` and tripping
	 *  P3's GLOBAL LOD-coarsening — which coarsens the very near-field detail P4 was meant to
	 *  refine (the "mountaintop goes blocky" bug).
	 *
	 *  3D cameras are LOCKED (no free pan/zoom), so the P3 "coarsen to fit" mechanism has no
	 *  benefit here: instead we HALT at the hard ceiling and surface a user-facing warning.  When
	 *  `haltAtCeiling` is true, `update()` (a) sets `lru.maxChunks = MAX_CHUNKS_HARD_CEILING`,
	 *  (b) SKIPS the coarsening-factor scaling (factor stays 1.0 → the P4 metric builds the correct
	 *  near-field tree), and (c) flags `renderLimitHit` when the tree's real eval demand exceeds
	 *  the ceiling; `dequeueForWorker()` then returns nothing once `chunkCount` reaches the ceiling,
	 *  halting eval (far chunks stay uneval'd rather than thrashing).  Absent → the P3 coarsening
	 *  path is unchanged (2D ortho: the constant metric makes the demand EXACT, so coarsening is
	 *  both correct and never spuriously triggered — R2 byte-identical). */
	haltAtCeiling?: boolean;
	// Set15 P6 (§3.7) — the 2:1-balance pass (`balance2to1`) was REMOVED (2026-08-19): the
	// generalized edge stitch (`edgeCoarserLevels`) handles any power-of-two level ratio via a
	// `2^k`-lerp, so the tree no longer needs to be force-balanced.  The balance pass caused a
	// perpetual split/merge oscillation (it split a coarse neighbour that `updateNode` then
	// merged back next frame, re-enqueueing its children forever) → the "Eval/Sample" phase never
	// settled.  The shader reads `u_edgeCoarserLevel` per edge; no balance is required.
	/** Set15.5 P6 — when `haltAtCeiling`, the flag set on the last `update()` when the tree's
	 *  eval demand reached/neared the hard ceiling (the renderer surfaces this as a red warning).
	 *  The renderer can also write it back to drive a UI badge; the quadtree itself just reports
	 *  the internal state. */
	renderLimitHit?: {set: (v: boolean)=>void; get: ()=>boolean};
	/** Camera center in world space — used to prioritise nearby chunks. */
	cameraCenter: {x: number, y: number};
	/** Visible world-space AABB (already expanded by the desired margin). */
	viewportBounds: Rect;
	/** Set15.5 P2 (§4) — the lattice FLOOR: the minimum leaf width (metres).  A leaf is not allowed
	 *  to get narrower than this, so the render sample interval `leafWidth/32` cannot get finer than
	 *  the solved fine-cell size (the realized cell / setting).  The caller resolves the validated
	 *  clamp (via `resolveRenderChunkMaxSamplesPerFineCellEdge` — R1) and multiplies by `32` (§4.1:
	 *  CHUNK_N − 1 = 32 intervals across 33 samples).  Absent → no floor (2D ortho stays byte-identical;
	 *  the ortho lattice is already screen-coupled to the solve, §9).  It applies as a split/merge
	 *  floor in `updateNode`, OVERRIDING `splitThreshold` where it binds (§4.3). */
	minLeafWidth?: number;
	/** Set15.5 P2 (§4.2) — the REALIZED solved fine-cell size (metres) as a STRING-keyed cache so the
	 *  updateNode floor (P4's elevation-aware metric) can read the value WITHOUT importing the stats
	 *  singleton (which drags mobx/mobx-graphlink CJS — see the TwoGridCache header).  Keyed by terrain
	 *  region id; each entry is the realized cell for that region (falls back to the caller's
	 *  `realizedFineCellSize` on the whole-tree pass, resolved R1).  P2 reads it directly; P4's
	 *  ancestor-height sampling walks this same cache to bound the elevation-aware split. */
	realizedCellSize?: ReadonlyMap<string, number>;
	/** Set15.5 P2 — the validated resolved clamp range for the floor setting (metres), precomputed by
	 *  the caller so the quadtree does not re-resolve it per node.  `minSampleSpacing = solvedCell /
	 *  setting`, `minLeafWidth = 32 × minSampleSpacing`; the setting itself is clamped `[0.25, 64]`
	 *  (R1).  Present on the perspective branch only. */
	renderFloor?: {minSampleSpacing: number; minLeafWidth: number};
	/** Set15.5 P3 (§5.3 / R5) — the per-frame LOD-coarsening factor IN, factor OUT.  `update()`
	 *  writes the smoothed `lodCoarseningFactor` here so the caller can persist it (e.g. into
	 *  `TerrainProcessStats.lodCoarseningFactor`, §7) and read the previous frame's value (for the
	 *  exponential-smoothing hysteresis that prevents frame-to-frame LOD pumping).  Absent → the
	 *  factor is assumed 1.0 (no coarsening — ortho, or a caller that opted out). */
	lodCoarseningFactor?: {set: (v: number)=>void; get: ()=>number};
};

/** An axis-aligned rectangle defining one terrain region in world space. */
export interface TerrainRegionRect {
	terrainID: string;
	rect: Rect;
}

export class TerrainQuadtree {
	root: QuadtreeNode;
	private lru = new LRUCache();
	/** Nodes awaiting chunk evaluation, in priority order (front = next to evaluate). */
	private evalQueue: QuadtreeNode[] = [];
	/** Nodes awaiting texture build, in priority order (front = next to build). */
	private textureQueue: QuadtreeNode[] = [];

	constructor(rootBounds: Rect) {
		this.root = new QuadtreeNode(rootBounds, 0);
	}

	/** Test/debug accessor — the LRU cap resolved by the last `update()` call (R1 guard).
	 *  Exposed so the P1 regression test can assert the cap is applied without reaching into the
	 *  private LRU.  Set15.5 P3 (§5.2): now `min(demand formula, MAX_CHUNKS_HARD_CEILING)`. */
	get maxChunks_resolved(): number {
		return this.lru.maxChunks;
	}

	/** Set15.5 P5 (§7) — the RAW LOD demand computed by the last `update()` call (before any
	 *  clamping to `MAX_CHUNKS_HARD_CEILING`): `ceil(vbScreenW/mergeThreshold) ·
	 *  ceil(vbScreenH/mergeThreshold) · LRU_SAFETY_MULTIPLIER`, evaluated at the viewport centre.
	 *  Surfaces the budget's headroom vs the ceiling (the `demandedLeafCount` stat) and lets the
	 *  agent observe P1's cull gain + P3's budget binding directly.  `undefined` until the first
	 *  `update()`. */
	private rawDemand: number | null = null;
	/** Set15.5 P5 (§7) — see `rawDemand`.  Exposed as a getter so the renderer's diagnostics pass
	 *  can read it without importing the private field (the `demandedLeafCount` stat). */
	get demandedLeafCount(): number | null { return this.rawDemand; }

	/** Set15.5 P5 (§7) — the number of LIVE chunks in the LRU (evaluated/being-evaluated leaves).
	 *  O(1) via the LRU's maintained `count`.  Feeds the `chunkCount` stat (the churn canary vs
	 *  `MAX_CHUNKS_HARD_CEILING` — cf. the `cameraGen` churn canary in Set15 P5). */
	get chunkCount(): number { return this.lru.size(); }

	/** Set15.5 P5 (§7) — the width (metres) of the DEEPEST leaf covering world (wx, wy).  Under
	 *  perspective the render lattice spacing at a point = `leafWidth / 32` (CHUNK_N − 1 = 32
	 *  intervals across 33 samples, §4.1) — the number that was 64× too coarse in §1.1.  The
	 *  renderer samples this at the eye (view-centre) to produce the `renderLatticeSpacing` stat,
	 *  directly comparable to `fineCellSize_final`.  Returns `null` when the point is outside the
	 *  tree's root (degenerate / pre-init). */
	leafWidthAtPoint(wx: number, wy: number): number | null {
		// TS 7 control-flow note: the width is captured directly (not via a `leaf` variable that a
		// callback closure narrows to `never` after the call — that pattern fails `leaf.bounds`).
		let width: number | null = null;
		this.findLeafForPoint(this.root, wx, wy, node=>{ width = node.bounds.width; });
		return width;
	}

	/** Set15.5 P5 (§7) — the number of leaves whose bounds intersect `viewportBounds` (the 
	 *  frustum-culled visible set).  Cheap O(leaves) walk; used for the `visibleLeafCount` stat
	 *  that makes P1's cull gain observable. */
	countVisibleLeaves(viewportBounds: Rect): number {
		let n = 0;
		for (const leaf of this.collectLeaves()) {
			if (leaf.bounds.Intersects(viewportBounds)) n++;
		}
		return n;
	}

	/** Set15.5 P3 (§5.3 / R5) — the smoothed LOD-coarsening factor (≥ 1).  1.0 = no coarsening
	 *  (the budget is not binding).  Persists across frames so the hysteresis below has state. */
	private lodCoarseningFactor = 1;

	/** Set15.5 P6 (2026-08-19) — whether the CURRENT camera is in 3D halt mode (set by `update()`
	 *  from `viewport.haltAtCeiling`).  When true, `dequeueForWorker` halts at the hard ceiling
	 *  instead of evicting/re-coarsening.  Mirrors the field on the last `ViewportInfo` so the
	 *  queue-level halt check in `dequeueForWorker` doesn't need the viewport re-passed. */
	private haltAtCeiling = false;

	/** Update split/merge state. Nodes that need evaluation are queued but NOT evaluated here.
	 *  Only nodes that intersect at least one region rect are enqueued.
	 *  @param maxPxPerSample — LOD threshold: chunk splits when screen-px-per-sample exceeds this.
	 *
	 *  Set15.5 P3 (§5.3) — graceful degradation instead of evict-thrash.  The raw demand formula
	 *  (`vbScreenW/mergeThreshold · vbScreenH/mergeThreshold · LRU_SAFETY_MULTIPLIER`, evaluated at
	 *  the viewport centre) estimates how many chunks the LOD metric wants.  When that exceeds
	 *  `MAX_CHUNKS_HARD_CEILING`, the split threshold is scaled up by `sqrt(demand/ceiling)` (leaf
	 *  count ∝ threshold⁻²) so the tree GLOBALLY coarsens to fit — making the §1.5 evict→re-enqueue
	 *  thrash structurally impossible rather than cap-dependent.  The factor is exponential-smoothed
	 *  (`LOD_COARSEN_HYSTERESIS`) so a static viewpoint converges to a fixed point without
	 *  frame-to-frame LOD pumping (R5). */
	update(viewport: ViewportInfo, regions: TerrainRegionRect[], maxPxPerSample: number) {
		const splitThreshold = CHUNK_N * maxPxPerSample;
		const mergeThreshold = splitThreshold / 2;

		// Size the LRU to fit all chunks that could be visible at this LOD + viewport, with margin for panning.
		// P2 (§3.5): under perspective `worldToScreenAt` varies, so the screen-size of the viewport is
		// not a single number — evaluate at the viewport CENTRE as the representative metric for the
		// LRU sizing.  Ortho: the function is constant, so this equals the old `viewportBounds.width *
		// viewport.worldToScreen` byte-for-byte.
		const vbWts = viewport.worldToScreenAt(
			viewport.viewportBounds.x + viewport.viewportBounds.width / 2,
			viewport.viewportBounds.y + viewport.viewportBounds.height / 2,
		);
		const vbScreenW = viewport.viewportBounds.width * vbWts;
		const vbScreenH = viewport.viewportBounds.height * vbWts;
		const chunksX = Math.ceil(vbScreenW / mergeThreshold);
		const chunksY = Math.ceil(vbScreenH / mergeThreshold);
		const rawMaxChunks = Math.max(64, chunksX * chunksY * LRU_SAFETY_MULTIPLIER);
		// Set15.5 P5 (§7) — surface the raw demand for the `demandedLeafCount` stat (before
		// clamping to the ceiling — so the stat shows the TRUE budget headroom).
		this.rawDemand = rawMaxChunks;

		// Set15.5 P6 (2026-08-19) — under 3D (`haltAtCeiling`), the demand estimate is unreliable
		// (single-centre sample inflated by P4's elevation-aware metric), so it must NOT drive the
		// budget.  Instead pin the LRU at the hard ceiling and rely on `dequeueForWorker` to halt
		// eval when the real tree fills it.  Set the flag when the (unreliable) estimate OR the
		// tree's actual leaf demand is at the ceiling — the renderer warns the user either way.
		const halt = viewport.haltAtCeiling === true;
		this.haltAtCeiling = halt;
		if (halt) {
			this.lru.maxChunks = MAX_CHUNKS_HARD_CEILING;
		} else {
			// The LRU budget is the RAW demand clamped to the hard ceiling (§5.2 OOM guard).
			this.lru.maxChunks = Math.min(rawMaxChunks, MAX_CHUNKS_HARD_CEILING);
		}

		// Set15.5 P3 (§5.3) — graceful coarsening.  Gated on `viewport.lodCoarseningFactor` being
		// present (the renderer supplies it ONLY under perspective): when it is ABSENT (ortho) the
		// factor is forced to 1 and the internal state is untouched, so 2D stays byte-identical
		// (R2) — a 3D→2D mode switch must not carry over an elevated factor into the ortho split
		// decisions.  When present: target = 1 when the raw demand fits the hard ceiling, else the
		// threshold scale (sqrt of demand/ceiling, since count ∝ threshold⁻²) that brings the tree
		// under it.  Exponentially smoothed (`LOD_COARSEN_HYSTERESIS`) so a static view converges to
		// a fixed point without frame-to-frame LOD pumping (R5).
		// Set15.5 P6 — under 3D halt mode the coarsening is SKIPPED entirely (factor forced to 1):
		// the estimate that would drive it is unreliable, and a locked 3D camera doesn't need
		// coarsen-to-fit (we halt instead).  Without this, a spurious demand > ceiling would still
		// coarsen the tree and re-introduce the blocky near-field peak.
		let factor = 1;
		if (viewport.lodCoarseningFactor && !halt) {
			const targetFactor = rawMaxChunks > MAX_CHUNKS_HARD_CEILING
				? Math.sqrt(rawMaxChunks / MAX_CHUNKS_HARD_CEILING)
				: 1;
			this.lodCoarseningFactor = this.lodCoarseningFactor + (targetFactor - this.lodCoarseningFactor) * LOD_COARSEN_HYSTERESIS;
			factor = Math.max(1, this.lodCoarseningFactor);
			// Surface the smoothed factor so the caller can persist it (e.g. the `lodCoarseningFactor`
			// stat, §7).  `factor` never goes below 1 (coarsen-only).
			viewport.lodCoarseningFactor.set(factor);
		}

		// The tree splits at `splitThreshold * factor` (count ∝ factor⁻²), producing ≈
		// rawMaxChunks/factor² ≤ MAX_CHUNKS_HARD_CEILING chunks — so the LRU never has to evict a
		// chunk that `updateNode` will immediately re-enqueue (§5.3 / §1.5 fix).  Under ortho
		// factor = 1 → byte-identical split decisions.
		const effSplitThreshold = splitThreshold * factor;
		const effMergeThreshold = mergeThreshold * factor;
		this.updateNode(this.root, viewport, regions, effSplitThreshold, effMergeThreshold, viewport.minLeafWidth);

		// Set15.5 P6 — report whether the tree's actual eval demand hit the ceiling (in 3D halt
		// mode).  The demand here is the number of leaves still QUEUED plus those already evaluated
		// (this.evalQueue.length + lru.size()) — i.e. what the tree genuinely wants to evaluate,
		// independent of the (unreliable) centre-sample estimate.  If it's at/near the ceiling, the
		// halt is about to/just engaged → surface the red warning.
		if (viewport.renderLimitHit) {
			const totalEvalDemand = this.evalQueue.length + this.lru.size();
			const hit = halt && totalEvalDemand >= MAX_CHUNKS_HARD_CEILING;
			viewport.renderLimitHit.set(hit);
		}

		// Sort both queues so chunks nearest the camera are processed first
		const cx = viewport.cameraCenter.x;
		const cy = viewport.cameraCenter.y;
		const distSq = (n: QuadtreeNode) => {
			const nx = n.bounds.x + n.bounds.width / 2;
			const ny = n.bounds.y + n.bounds.height / 2;
			return (nx - cx) ** 2 + (ny - cy) ** 2;
		};
		this.evalQueue.sort((a, b)=>distSq(a) - distSq(b));
		this.textureQueue.sort((a, b)=>distSq(a) - distSq(b));
	}

	/** Dequeue up to `maxN` pending nodes for off-thread evaluation.
	 *  Marks each chunk as inflight so duplicate dispatch is prevented.
	 *  Returns an empty array if the queue is empty.
	 *
	 *  Set15.5 P6 (2026-08-19) — in 3D halt mode (`haltAtCeiling`), once the LRU is at/over the hard
	 *  ceiling this returns NOTHING: eval halts, and the renderer's red warning (driven by
	 *  `renderLimitHit`) tells the user their viewpoint/viewer settings demand more chunks than the
	 *  hard limit allows.  The near-field leaves are enqueued nearest-first (sorted in `update()`),
	 *  so the chunks that DID get in before the halt are the closest to the camera — the user sees
	 *  near-field detail and a warning for the uneval'd far field, with NO evict→re-enqueue thrash
	 *  (we never evict-and-reevaluate; we stop).  In 2D this is a no-op (the demand estimate is
	 *  exact there, so the tree never exceeds the ceiling). */
	dequeueForWorker(maxN: number): QuadtreeNode[] {
		const out: QuadtreeNode[] = [];
		// P6 — halt at the ceiling in 3D: don't allocate/evaluate chunks beyond the hard limit.
		// (The LRU enforces the same bound on eviction, but halting here prevents even the transient
		// over-allocate + re-enqueue that the pre-P6 coarsening used to avoid.)
		if (this.haltAtCeiling && this.lru.size() >= this.lru.maxChunks) return out;
		while (out.length < maxN && this.evalQueue.length > 0) {
			const node = this.evalQueue.shift()!;
			if (!node.isLeaf) continue; // merged since enqueue — skip
			if (!node.chunk) node.chunk = new Chunk();
			node.chunk.dirty_data = false; // cleared optimistically; re-set to true by markAllDirty if called mid-flight
			node.chunk.inflight = true;
			out.push(node);
		}
		return out;
	}

	/** 2026-09-01 materials-first — dequeue up to `maxN` NEVER-EVALUATED leaves (texVersion === 0)
	 *  for the flat pre-pass, leaving already-evaluated leaves (texVersion >= 1) in the queue.
	 *  A never-evaluated chunk has only zero-initialized arrays, so it renders nothing until filled;
	 *  the flat pre-pass fills exactly these.  An already-evaluated chunk carries REAL solved
	 *  heights and must KEEP them until the new solve's eval lands (a viewpoint switch re-enqueues
	 *  it via markAllDirty; flattening it would drop it below the 3D viewport).  Shares the 3D
	 *  halt-ceiling gate with `dequeueForWorker`.
	 *
	 *  CRITICAL (2026-09-01): we MUST clear `dirty_data` here, mirroring `dequeueForWorker`. The
	 *  flat fill commits SYNCHRONOUSLY in the same tick (`applyWorkerResult` is called right after
	 *  this returns, with no `markAllDirty` in between), so clearing is safe — and leaving it set
	 *  is a deadlock: `applyWorkerResult` sees `dirty_data` and re-enqueues the node WITHOUT
	 *  committing (stays `texVersion 0`), so it's re-flat-filled and re-enqueued forever. The
	 *  queue never drains and the "fake solve" never lands. */
	dequeueForFlatFill(maxN: number): QuadtreeNode[] {
		const out: QuadtreeNode[] = [];
		if (this.haltAtCeiling && this.lru.size() >= this.lru.maxChunks) return out;
		let scanned = 0;
		while (out.length < maxN && scanned < this.evalQueue.length) {
			const node = this.evalQueue[scanned];
			// A node with no chunk yet, or a chunk that has never been evaluated (texVersion === 0),
			// has only zero-initialized arrays and is flat-fillable.  Allocate the chunk if absent.
			const isNeverEvaluated = node.isLeaf && (!node.chunk || node.chunk.texVersion === 0);
			if (isNeverEvaluated) {
				if (!node.chunk) node.chunk = new Chunk();
				// The flat fill commits synchronously this tick; clear so `applyWorkerResult` commits
				// instead of re-enqueuing (see the deadlock note above). Not inflight — no async gap.
				node.chunk.dirty_data = false;
				// Remove this node from the queue (it's committed to the flat fill now).
				this.evalQueue.splice(scanned, 1);
				out.push(node);
			} else {
				scanned++;
			}
		}
		return out;
	}

	/** Worker finished evaluating a chunk. If `aborted` (2026-07-25 zoom-overtake fix: worker's
	 *  evalChunk catch zero-filled heights + posted `aborted:true`; pre-fix that flat-0 was accepted as
	 *  real → permanent black, never re-enqueued) or `dirty_data` (markAllDirty mid-flight) → re-enqueue. */
	applyWorkerResult(node: QuadtreeNode, heights: Float32Array, normals: Float32Array, aborted?: boolean, ambientWeightMask?: Float32Array | null, geoHeightGeom?: Float32Array) {
		if (!node.chunk) return; // evicted while inflight — discard
		node.chunk.inflight = false;
		if (aborted) {
			// Worker's evalChunk catch fired SolveAbortedError (a later zoom overtook its setCamera): heights are a
			// zero-fill — DON'T commit (no heights write, no dirty_texture); just re-mark dirty + re-enqueue. Pre-fix,
			// the chunk stayed dirty_data=false (cleared optimistically in dequeueForWorker) + flat-0 forever (permanent black).
			node.chunk.dirty_data = true;
			// Don't bump dirty_texture here — the chunk's existing texture stays until fresh heights land
			// (avoids a one-frame black flash on rendered-then-aborted chunks). When the fresh heights arrive,
			// the replay path applyWorkerResult(node, ..., false) sets dirty_texture=true + bumps texVersion as usual.
			this.enqueue(node);
			return;
		}
		if (node.chunk.dirty_data) {
			// markAllDirty() was called while eval was in-flight: result is stale — re-enqueue.
			this.enqueue(node);
			return;
		}
		node.chunk.heights = heights as Float32Array<ArrayBuffer>;
		node.chunk.normals = normals as Float32Array<ArrayBuffer>;
		// Set15 P3 (§3.4) — the D-less displacement lattice (undefined/absent on legacy callers
		// → leave the previous value; the batch path always ships it).
		if (geoHeightGeom) node.chunk.geoHeightGeom = geoHeightGeom as Float32Array<ArrayBuffer>;
		// 2026-08-08 (issue 2) — the per-chunk ambient-weight mask (null = no multiplier rings).
		node.chunk.ambientWeightMask = ambientWeightMask ?? null;
		node.chunk.dirty_texture = true;
		node.chunk.texVersion++;
		this._enqueueTexture(node);
		this.lru.touch(node);
	}

	private updateNode(node: QuadtreeNode, viewport: ViewportInfo, regions: TerrainRegionRect[], splitThreshold: number, mergeThreshold: number, minLeafWidth?: number) {
		// Cull subtrees outside the expanded viewport
		if (!node.bounds.Intersects(viewport.viewportBounds)) {
			if (!node.isLeaf) this.merge(node);
			return;
		}

		// Cull entire subtrees that don't touch any terrain region
		if (regions.length > 0 && !regions.some(r=>node.bounds.Intersects(r.rect))) {
			// Merge children if present (no need to maintain sub-structure outside regions)
			if (!node.isLeaf) this.merge(node);
			return;
		}

		// Set15 P2 (§3.5) — per-node screen size: evaluate `worldToScreenAt` at the node CENTRE so
		// the perspective metric (larger far away) drives the split/merge decision correctly.  For
		// ortho the function is constant, so this is byte-identical to `node.bounds.width *
		// viewport.worldToScreen` (the §9 ortho-parity guarantee).
		const nodeCenterX = node.bounds.x + node.bounds.width / 2;
		const nodeCenterY = node.bounds.y + node.bounds.height / 2;
		const screenSize = node.bounds.width * viewport.worldToScreenAt(nodeCenterX, nodeCenterY);

		if (node.isLeaf) {
			// Set15.5 P2 (§4) — the lattice FLOOR: never split below `minLeafWidth`, EVEN IF the
			// px-per-sample metric demands it.  `minLeafWidth` (from `viewport.minLeafWidth`,
			// absent under ortho) OVERRIDES `splitThreshold` when it binds — you cannot sample finer
			// than the solved field (§4.3).  This is also the tree's ONLY depth guard (§4.4): without
			// it, P4's elevation-aware metric (terrainH → eye height ⇒ d → 0 ⇒ w → ~866k px/m) would
			// demand a ~0.00015 m leaf and split unboundedly.
			// A split is allowed only if the RESULTING children (width/2) still respect the floor —
			// so no sub-floor leaf is ever created (guarantees the bounded-depth property).
			if (screenSize > splitThreshold && (minLeafWidth == null || node.bounds.width / 2 >= minLeafWidth)) {
				this.split(node);
				for (const child of node.children!) this.updateNode(child, viewport, regions, splitThreshold, mergeThreshold, minLeafWidth);
			} else {
				if (!node.chunk || node.chunk.dirty_data) this.enqueue(node);
			}
		} else {
			const maxChildScreen = node.bounds.width / 2 * viewport.worldToScreenAt(nodeCenterX, nodeCenterY);
			// Set15.5 P2 (§4.5) — the merge branch must respect the SAME floor: a floored leaf (one
			// kept because splitting would create sub-floor children) must not be split-merged in a
			// loop.  Since no sub-floor leaf is ever created (the split guard above), merging is
			// blocked only when the resulting children (width/2) would be sub-floor — which can't
			// happen if the tree is consistent, so this is a symmetric safety net, not the fix.
			if (maxChildScreen < mergeThreshold && (minLeafWidth == null || node.bounds.width / 2 >= minLeafWidth)) {
				this.merge(node);
				if (!node.chunk || node.chunk.dirty_data) this.enqueue(node);
			} else {
				for (const child of node.children!) this.updateNode(child, viewport, regions, splitThreshold, mergeThreshold, minLeafWidth);
			}
		}
	}

	private enqueue(node: QuadtreeNode) {
		if (node.chunk?.inflight) return; // already being processed in a worker
		if (!this.evalQueue.includes(node)) this.evalQueue.push(node);
	}

	/** Set15 P6 (§3.7) — find the DEEPEST leaf containing world (wx, wy).  A point exactly on a
	 *  leaf boundary is contained by multiple leaves; this returns the deepest (finest) one.
	 *  Used by the balance pass + the stitch-flag computation to find edge-neighbours.  Returns
	 *  null when the point is outside the tree's root. */
	findDeepestLeafAt(wx: number, wy: number): QuadtreeNode | null {
		let deepest: QuadtreeNode | null = null;
		this.findLeafForPoint(this.root, wx, wy, n=>{
			if (deepest == null || n.depth > deepest.depth) deepest = n;
		});
		return deepest;
	}

	/** Set15 P6 (§3.7) — the four edge-neighbour query points for a leaf's bounds, in
	 *  [south, east, north, west] order.  Each is a QUARTER-POINT along the edge offset slightly
	 *  OUTWARD (not the midpoint — a finer neighbour's subdivisions place an internal boundary at
	 *  the midpoint, so the quarter-point is unambiguously inside one neighbour leaf).  Consumed
	 *  by {@link edgeCoarserLevels}.  Returned as [x, y] pairs (an Array<[number, number]> for
	 *  the for-of destructuring above). */
	private _edgeQueryPoints(b: Rect): Array<[number, number]> {
		const w = b.width;
		const eps = w * 0.01;
		return [
			[b.x + w * 0.25, b.y - eps],      // south (y−)
			[b.x + w + eps,  b.y + w * 0.25], // east  (x+)
			[b.x + w * 0.25, b.y + w + eps],  // north (y+)
			[b.x - eps,      b.y + w * 0.25], // west  (x−)
		];
	}

	/** Set15 P6 (§3.7) — per-edge COARSENESS for a leaf, in [south, east, north, west] order:
	 *  the number of levels the edge's neighbour is COARSER (shallower depth), or 0 if the neighbour
	 *  is same-depth or FINER.  The shader's edge stitch uses this delta `k` to compute the coarse
	 *  sample stride `2^k` and lerp between the two nearest coarse samples — which are the fine
	 *  chunk's OWN lattice samples at indices that are multiples of `2^k` (the quadtree is aligned),
	 *  so the stitch works for ANY power-of-two level ratio with no neighbour data and no 2:1-balance
	 *  requirement (a `2^k:1` neighbour's samples land on the fine chunk's `2^k`-spaced even indices).
	 *  The renderer maps this to the `u_edgeCoarserLevel` vec4 uniform per 3D chunk.  A neighbour
	 *  that is SAME depth or FINER gets 0 — it stitches its own edge to us.  Corners are always 0
	 *  (a corner index 0/32 is a multiple of every `2^k`, i.e. a shared coarse sample — never stitched). */
	edgeCoarserLevels(leaf: QuadtreeNode): [number, number, number, number] {
		const out: [number, number, number, number] = [0, 0, 0, 0];
		const pts = this._edgeQueryPoints(leaf.bounds);
		for (let i = 0; i < 4; i++) {
			const n = this.findDeepestLeafAt(pts[i][0], pts[i][1]);
			if (n && n.depth < leaf.depth) out[i] = leaf.depth - n.depth;
		}
		return out;
	}
	private split(node: QuadtreeNode) {
		const {x, y, width, height} = node.bounds;
		const hw = width / 2, hh = height / 2;
		node.children = [
			new QuadtreeNode(new Rect(x,      y,      hw, hh, false), node.depth + 1),
			new QuadtreeNode(new Rect(x + hw, y,      hw, hh, false), node.depth + 1),
			new QuadtreeNode(new Rect(x,      y + hh, hw, hh, false), node.depth + 1),
			new QuadtreeNode(new Rect(x + hw, y + hh, hw, hh, false), node.depth + 1),
		];
		// Set15.5 P3 (§5.4) — `remove` fires `onEvict`, which tears down the parent's GPU textures.
		// This is CORRECT for memory (the renderer keys its texture maps by `QuadtreeNode`; once a
		// node splits it is no longer a leaf, so its textures must be released or they leak).  The
		// §5.4 concern is only that rapid split/merge CYCLING churns GPU allocations — that is
		// mitigated by the P3 §5.3 hysteresis (LOD_COARSEN_HYSTERESIS) + P4, NOT by skipping
		// onEvict (which would leak the split-away node's textures).
		if (node.chunk) {
			// Set16 fix (2026-08-19) — RETAIN the parent's D-LESS height lattice as a stable,
			// non-GPU height source BEFORE destroying the chunk.  Without this, the freshly-split
			// children lose the only evaluated ancestor's height (§6.2) → the elevation-aware
			// metric collapses to `dz = eyeZ` → the tree merges back → re-evaluates → splits again:
			// the §6.2 split/merge churn (confirmed live: `internalWithChunk` was always 0).
			// The ancestor sampler reads `retainedGeoHeightGeom` in place of the gone chunk, so the
			// children keep a stable height until they evaluate (convergence, not oscillation).
			if (!node.chunk.dirty_data && !node.chunk.inflight) {
				node.retainedGeoHeightGeom = node.chunk.geoHeightGeom;
			}
			this.lru.remove(node);
		}
	}

	private merge(node: QuadtreeNode) {
		if (node.children) {
			for (const child of node.children) {
				if (!child.isLeaf) this.merge(child);
				// Set15.5 P3 (§5.4) — `remove` (fires onEvict) so the child's GPU textures are
				// released when it folds back up.  Same rationale as split(): memory-correct, and
				// the split/merge-cycling GPU churn is bounded by the P3/P4 hysteresis.
				if (child.chunk) this.lru.remove(child);
				// Set16 fix — the child's RETAINED height source is now stale (the child is being
				// folded up and discarded).  Release it so a later re-split of this subtree doesn't
				// resurrect an old height.  The merged-up node (`node`) re-evaluates as a leaf below.
				child.retainedGeoHeightGeom = null;
				const qi = this.evalQueue.indexOf(child);
				if (qi !== -1) this.evalQueue.splice(qi, 1);
				const ti = this.textureQueue.indexOf(child);
				if (ti !== -1) this.textureQueue.splice(ti, 1);
			}
			node.children = null;
			// Set16 fix — `node` is now a LEAF and will re-evaluate; drop any retained height source
			// it carried as an internal node so the fresh eval is authoritative.
			node.retainedGeoHeightGeom = null;
		}
	}

	/** Mark all chunks dirty (data + texture) and re-enqueue them for evaluation. */
	markAllDirty(node = this.root) {
		if (node.chunk) {
			node.chunk.dirty_data = true;
			node.chunk.dirty_texture = true;
			this.enqueue(node);
		}
		if (node.children) for (const c of node.children) this.markAllDirty(c);
	}

	/** 2026-09-01 materials-first — re-enqueue a SPECIFIC set of leaf nodes for a fresh data eval
	 *  (dirty_data + dirty_texture) WITHOUT touching the rest of the tree.  Used to upgrade
	 *  flat-filled leaves to their real solved heights once the height solve lands, while leaving
	 *  already-evaluated neighbours (which keep their real heights) untouched.  Skips nodes that
	 *  are no longer leaves (split since the flat fill), already in-flight, or already dirty. */
	markNodesDirty(nodes: QuadtreeNode[]) {
		for (const node of nodes) {
			if (!node.isLeaf || !node.chunk || node.chunk.inflight || node.chunk.dirty_data) continue;
			node.chunk.dirty_data = true;
			node.chunk.dirty_texture = true;
			this.enqueue(node);
		}
	}

	/** Mark all evaluated chunks as needing texture regeneration (without noise re-evaluation). */
	markAllDirtyTexture(node = this.root) {
		if (node.chunk && !node.chunk.dirty_data) {
			node.chunk.dirty_texture = true;
			node.chunk.texVersion++;
			this._enqueueTexture(node);
		}
		if (node.children) for (const c of node.children) this.markAllDirtyTexture(c);
	}

	/** Collect all leaf nodes (both evaluated and pending). */
	collectLeaves(out: QuadtreeNode[] = [], node = this.root): QuadtreeNode[] {
		if (node.isLeaf) {
			out.push(node);
		} else {
			for (const c of node.children!) this.collectLeaves(out, c);
		}
		return out;
	}

	/** True when at least one leaf has dirty_texture=true and is ready for a texture build dispatch. */
	get hasTextureDirty() { return this._hasTextureDirty; }
	private _hasTextureDirty = false;

	/** Number of leaves waiting on the eval queue. The renderer's camera-fill eval-phase timing (map-UI
	 *  Stats "Terrain Info") uses this to detect the queue-drained moment that ends an eval-fill window.
	 *  The test helpers below delegate to this same private field. */
	get evalQueueLength() { return this.evalQueue.length; }

	/** Set15.5 P6 — whether 3D halt mode is active (last `update()` ran with `viewport.haltAtCeiling`
	 *  true).  When true, `dequeueForWorker` stops pulling once the LRU reaches the hard ceiling and the
	 *  residual far-field leaves REMAIN in the eval queue forever (the deliberate halt-vs-coarsen design).
	 *  The renderer's eval-fill finaliser (`checkEvalFillDrained`) treats this as "the fill is complete"
	 *  — otherwise the never-drained residual queue would keep the "Eval/Sample" stat's eval-fill window
	 *  open forever (the "in flight" counter never settles) even though the eval phase is genuinely done
	 *  (`renderLimitHit` already tells the user the far field was intentionally left unevaluated). */
	get evalHaltedAtCeiling() { return this.haltAtCeiling && this.lru.size() >= this.lru.maxChunks; }

	/** **Test helper** — number of leaves on the eval queue. Used by the 2026-07-25 zoom-while-zoom-in-progress
	 *  regression test to assert an `aborted` chunk was re-enqueued (post-fix: length 0→1 on the abort replay;
	 *  pre-fix: stayed 0 → the permanent-black symptom). */
	get __testOnly_evalQueueLength() { return this.evalQueue.length; }

	/** **Test helper** — true if the given node is currently in the eval queue (waiting for the next
	 *  `dequeueForWorker`). Mirrors the `enqueue` membership check so the regression test can assert
	 *  "chunk was re-enqueued" without poking private state. O(n) — fine for tests; not a hot path. */
	__testOnly_isInEvalQueue(node: QuadtreeNode): boolean {
		return this.evalQueue.includes(node);
	}

	private _enqueueTexture(node: QuadtreeNode) {
		if (node.chunk?.texInflight) return; // already being built
		if (!this.textureQueue.includes(node)) {
			this.textureQueue.push(node);
			this._hasTextureDirty = true;
		}
	}

	/** Dequeue up to `maxN` leaves whose texture needs rebuilding (dirty_texture=true, not inflight).
	 *  Marks each chunk texInflight=true. */
	dequeueForTextureWorker(maxN: number): QuadtreeNode[] {
		if (!this._hasTextureDirty) return [];
		const out: QuadtreeNode[] = [];
		while (out.length < maxN && this.textureQueue.length > 0) {
			const node = this.textureQueue.shift()!;
			// Skip if evicted, re-dirtied for data, or already inflight from a re-enqueue
			if (!node.chunk || node.chunk.dirty_data || !node.chunk.dirty_texture || node.chunk.texInflight) continue;
			node.chunk.texInflight = true;
			out.push(node);
		}
		if (this.textureQueue.length === 0) this._hasTextureDirty = false;
		return out;
	}

	/** Called when the worker delivers a finished bitmap for a chunk. */
	applyTextureResult(node: QuadtreeNode) {
		if (!node.chunk) return;
		node.chunk.texInflight = false;
		node.chunk.dirty_texture = false;
	}

	/** **2026-07-28 inline texture bake.** Worker baked the texture inline (bitmap in the eval reply), so this cancels the
	 *  textureQueue entry applyWorkerResult enqueued + clears dirty_texture/texInflight (parity with applyTextureResult).
	 *  No-op if chunk evicted; returns false if re-dirtied for DATA (stale bitmap — caller closes it). */
	applyInlineTextureResult(node: QuadtreeNode): boolean {
		if (!node.chunk) return false;
		// remove the textureQueue entry applyWorkerResult's _enqueueTexture added
		const ti = this.textureQueue.indexOf(node);
		if (ti !== -1) this.textureQueue.splice(ti, 1);
		if (this.textureQueue.length === 0) this._hasTextureDirty = false;
		// If a data-dirty mark landed between the eval reply and this call, the bitmap is
		// stale — signal the caller to discard the bitmap (it will re-bake on the next
		// eval cycle).  Do NOT clear dirty_texture here — the next eval cycle rebakes.
		if (node.chunk.dirty_data) return false;
		// parity with applyTextureResult: clear inflight + dirty
		node.chunk.texInflight = false;
		node.chunk.dirty_texture = false;
		return true;
	}

	get pendingCount() { return this.evalQueue.length; }

	/** Sample terrain elevation at world (wx, wy): bilinear interpolation over the finest evaluated leaf chunk.
	 *  Returns null if no evaluated chunk covers the point yet. */
	sampleHeightAt(wx: number, wy: number): number|null {
		let best: QuadtreeNode|null = null;
		this.findLeafForPoint(this.root, wx, wy, node=>{
			if (node.chunk && !node.chunk.dirty_data && !node.chunk.inflight) {
				if (best == null || node.bounds.width < best.bounds.width) best = node;
			}
		});
		if (best == null) return null;
		const {x, y, width, height} = (best as QuadtreeNode).bounds;
		const heights = (best as QuadtreeNode).chunk!.heights;
		const N = CHUNK_N;
		const col_f = (wx - x) / width * (N - 1);
		const row_f = (wy - y) / height * (N - 1);
		const col0 = Math.max(0, Math.min(N - 2, Math.floor(col_f)));
		const row0 = Math.max(0, Math.min(N - 2, Math.floor(row_f)));
		const tc = col_f - col0, tr = row_f - row0;
		const h00 = heights[row0 * N + col0];
		const h10 = heights[row0 * N + col0 + 1];
		const h01 = heights[(row0 + 1) * N + col0];
		const h11 = heights[(row0 + 1) * N + col0 + 1];
		return h00 * (1 - tc) * (1 - tr) + h10 * tc * (1 - tr) + h01 * (1 - tc) * tr + h11 * tc * tr;
	}

	/** Sample the geometric normal lattice (worker's z-up normals, `chunk.normals`) at world
	 *  (wx, wy) — bilinear interpolation over the finest evaluated leaf chunk.  Returns an
	 *  {x, y, z} normal (normalized) or null if no evaluated chunk covers the point yet.  Mirrors
	 *  {@link sampleHeightAt}'s leaf-finding + bilinear walk (used by the splat rasteriser's
	 *  per-sample slope, which drives the material-region slope masks). */
	sampleNormalAt(wx: number, wy: number): {x: number, y: number, z: number}|null {
		let best: QuadtreeNode|null = null;
		this.findLeafForPoint(this.root, wx, wy, node=>{
			if (node.chunk && !node.chunk.dirty_data && !node.chunk.inflight) {
				if (best == null || node.bounds.width < best.bounds.width) best = node;
			}
		});
		if (best == null) return null;
		const {x, y, width, height} = (best as QuadtreeNode).bounds;
		const normals = (best as QuadtreeNode).chunk!.normals;
		const N = CHUNK_N;
		const col_f = (wx - x) / width * (N - 1);
		const row_f = (wy - y) / height * (N - 1);
		const col0 = Math.max(0, Math.min(N - 2, Math.floor(col_f)));
		const row0 = Math.max(0, Math.min(N - 2, Math.floor(row_f)));
		const tc = col_f - col0, tr = row_f - row0;
		const lerp = (a: number, b: number, c: number, d: number)=>a * (1 - tc) * (1 - tr) + b * tc * (1 - tr) + c * (1 - tc) * tr + d * tc * tr;
		const i00 = (row0 * N + col0) * 3;
		const i10 = (row0 * N + col0 + 1) * 3;
		const i01 = ((row0 + 1) * N + col0) * 3;
		const i11 = ((row0 + 1) * N + col0 + 1) * 3;
		const nx = lerp(normals[i00], normals[i10], normals[i01], normals[i11]);
		const ny = lerp(normals[i00 + 1], normals[i10 + 1], normals[i01 + 1], normals[i11 + 1]);
		const nz = lerp(normals[i00 + 2], normals[i10 + 2], normals[i01 + 2], normals[i11 + 2]);
		const len = Math.hypot(nx, ny, nz) || 1;
		return {x: nx / len, y: ny / len, z: nz / len};
	}

	private findLeafForPoint(node: QuadtreeNode, wx: number, wy: number, visit: (n: QuadtreeNode)=>void) {
		const {x, y, width, height} = node.bounds;
		if (wx < x || wx > x + width || wy < y || wy > y + height) return;
		if (node.isLeaf) { visit(node); return; }
		for (const child of node.children!) this.findLeafForPoint(child, wx, wy, visit);
	}

	/** Sample the D-LESS displacement lattice (`geoHeightGeom`) at world (wx, wy) — the field the
	 *  3D vertex shader displaces terrain geometry by (`u_geoHeightGeom`).  Set15 P4 (§3.8) uses
	 *  this to sit each scenery billboard's base on the ACTUAL rendered surface (the terrain is
	 *  displaced by this lattice, NOT by `heights` — using `sampleHeightAt` would place trees
	 *  relative to the D-inclusive field and float/bury them by one ambient-detail band).  Returns
	 *  null if no evaluated chunk covers the point yet (the caller falls back to the coarse macro /
	 *  flat 0). */
	sampleGeoHeightGeomAt(wx: number, wy: number): number|null {
		let best: QuadtreeNode|null = null;
		this.findLeafForPoint(this.root, wx, wy, node=>{
			if (node.chunk && !node.chunk.dirty_data && !node.chunk.inflight) {
				if (best == null || node.bounds.width < best.bounds.width) best = node;
			}
		});
		if (best == null) return null;
		return this._sampleGeoHeightGeomFrom(best, wx, wy);
	}

	/** Set15.5 P4 (§6.2) — the ELEVATION-AWARE metric's height source: sample `geoHeightGeom` at
	 *  world (wx, wy), reading the FINEST node covering the point that has a valid chunk — the
	 *  node's OWN chunk when present, else the nearest ANCESTOR with one (a parent is always
	 *  evaluated before it splits, so an ancestor up the path carries a stable height while a
	 *  freshly-split child hasn't been evaluated yet).  **NEVER a fresh 0**: unlike
	 *  `sampleGeoHeightGeomAt` (which returns null unless the deepest leaf itself is evaluated),
	 *  this descends to the deepest valid chunk and only returns null when NOTHING along the path
	 *  is evaluated yet (the bootstrap state before the first chunk lands).  This is what keeps the
	 *  P4 metric stable across the split→eval→height-changes cycle (§6.2) — a just-split child
	 *  reads its parent's height rather than collapsing to eyeZ / 0.
	 *
	 *  (Why not reuse `sampleGeoHeightGeomAt`?  It stops at the DEEPEST LEAF and returns null if
	 *  that leaf has no chunk — but `split()` removes the parent's chunk when it splits (§5.4), so
	 *  a freshly-created child's nearest evaluated chunk lives on a GRANDPARENT, which the leaf-only
	 *  walk never reaches.  The ancestor walk below closes exactly that gap.) */
	sampleGeoHeightGeomAt_withAncestor(wx: number, wy: number): number|null {
		const node = this.findDeepestEvaluatedNodeForPoint(this.root, wx, wy);
		if (node == null) return null;
		return this._sampleGeoHeightGeomFrom(node, wx, wy);
	}

	/** 2026-09-01 (scenery Option B) — the STABLE scenery height source: sample `geoHeightGeom`
	 *  at world (wx, wy), reading the DEEPEST node covering the point that has ANY prior height
	 *  data — a chunk evaluated at least once (`texVersion > 0`), EVEN IF currently dirty/inflight
	 *  (after a `markAllDirty` re-solve / viewpoint switch), or a RETAINED lattice (a split-away
	 *  parent, §6.2).  This is the "keep old solved heights until the new solve lands" contract
	 *  for SCENERY: during a re-solve every chunk is dirty but each still carries its old
	 *  `geoHeightGeom`, so scenery keeps sitting on the previous surface instead of dropping to the
	 *  flat fallback (0) and falling out of the 3D viewport.  Returns null only when no node along
	 *  the path has ANY prior data yet (pre-bootstrap → the caller's flat-0 fallback is correct).
	 *  Unlike `sampleGeoHeightGeomAt`/`sampleGeoHeightGeomAt_withAncestor` (which intentionally
	 *  gate on NON-dirty chunks for the elevation-aware demand metric), this accepts dirty-but-
	 *  populated chunks — the right contract for scenery, whose Z must not flicker on re-solve. */
	sampleGeoHeightGeomAt_stable(wx: number, wy: number): number|null {
		const node = this.findDeepestPopulatedNodeForPoint(this.root, wx, wy);
		if (node == null) return null;
		return this._sampleGeoHeightGeomFrom(node, wx, wy);
	}

	/** Bilinear-sample `geoHeightGeom` from a node at world (wx, wy).
	 *  Shared by `sampleGeoHeightGeomAt` + `sampleGeoHeightGeomAt_withAncestor`.  Reads the node's
	 *  LIVE chunk lattice when present, else its RETAINED `geoHeightGeom` (the Set16 fix — the
	 *  parent's height kept across `split()`, §6.2 height source). */
	private _sampleGeoHeightGeomFrom(node: QuadtreeNode, wx: number, wy: number): number {
		const {x, y, width, height} = node.bounds;
		const heights = (node.chunk ?? {geoHeightGeom: node.retainedGeoHeightGeom!}).geoHeightGeom;
		const N = CHUNK_N;
		const col_f = (wx - x) / width * (N - 1);
		const row_f = (wy - y) / height * (N - 1);
		const col0 = Math.max(0, Math.min(N - 2, Math.floor(col_f)));
		const row0 = Math.max(0, Math.min(N - 2, Math.floor(row_f)));
		const tc = col_f - col0, tr = row_f - row0;
		const h00 = heights[row0 * N + col0];
		const h10 = heights[row0 * N + col0 + 1];
		const h01 = heights[(row0 + 1) * N + col0];
		const h11 = heights[(row0 + 1) * N + col0 + 1];
		return h00 * (1 - tc) * (1 - tr) + h10 * tc * (1 - tr) + h01 * (1 - tc) * tr + h11 * tc * tr;
	}

	/** Descend from `node` to the DEEPEST node covering (wx, wy) that has a valid height source —
	 *  either a live evaluated (non-dirty, not-inflight) chunk, or a RETAINED `geoHeightGeom`
	 *  (the Set16 fix: the parent's height kept across `split()`, §6.2).  This is the node's own
	 *  source when the covering leaf is evaluated, else the nearest ANCESTOR with one (§6.2 —
	 *  never a fresh 0).  Returns null only when no node along the path has ANY height source yet
	 *  (pre-bootstrap). */
	private findDeepestEvaluatedNodeForPoint(node: QuadtreeNode, wx: number, wy: number): QuadtreeNode|null {
		const {x, y, width, height} = node.bounds;
		if (wx < x || wx > x + width || wy < y || wy > y + height) return null;
		// A node is a valid height source if it has a live evaluated chunk OR a retained lattice
		// (a split-away parent whose chunk was destroyed but whose geoHeightGeom was kept — §6.2
		// stability: a freshly-split child reads the retained parent height rather than null→eyeZ).
		const selfValid = (node.chunk != null && !node.chunk.dirty_data && !node.chunk.inflight)
			|| node.retainedGeoHeightGeom != null;
		if (!node.isLeaf) {
			// Descend into children first — the deepest (finest) evaluated chunk wins.
			for (const child of node.children!) {
				const r = this.findDeepestEvaluatedNodeForPoint(child, wx, wy);
				if (r != null) return r;
			}
		}
		// No evaluated descendant → fall back to this node's own height source (nearest ancestor), if valid.
		return selfValid ? node : null;
	}

	/** 2026-09-01 (scenery Option B) — descend from `node` to the DEEPEST node covering (wx, wy)
	 *  that has ANY prior height data: a chunk evaluated at least once (`texVersion > 0`, dirty or
	 *  not — the old lattice is still valid until `applyWorkerResult` replaces it), or a RETAINED
	 *  `geoHeightGeom` (split-away parent, §6.2).  This is the scenery analogue of
	 *  `findDeepestEvaluatedNodeForPoint`, but relaxed to accept DIRTY-but-populated chunks, so a
	 *  re-solve (which dirties every chunk) does not collapse scenery to the flat fallback.  Returns
	 *  null only when no node along the path has ANY prior data yet (pre-bootstrap). */
	private findDeepestPopulatedNodeForPoint(node: QuadtreeNode, wx: number, wy: number): QuadtreeNode|null {
		const {x, y, width, height} = node.bounds;
		if (wx < x || wx > x + width || wy < y || wy > y + height) return null;
		// A node is a valid height source if it has a chunk evaluated at least once (its old
		// lattice holds real data even while dirty/inflight) OR a retained lattice (§6.2).
		const selfValid = (node.chunk != null && node.chunk.texVersion > 0)
			|| node.retainedGeoHeightGeom != null;
		if (!node.isLeaf) {
			// Descend into children first — the deepest (finest) populated chunk wins.
			for (const child of node.children!) {
				const r = this.findDeepestPopulatedNodeForPoint(child, wx, wy);
				if (r != null) return r;
			}
		}
		// No populated descendant → fall back to this node's own height source (nearest ancestor), if valid.
		return selfValid ? node : null;
	}

	dispose() {
		this.evalQueue = [];
		this.textureQueue = [];
		this.lru.clear();
	}
}
